import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { EditorLabel } from '../EditorLabel';
import { IEditorProps } from '../Editor';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { getChildValueAsString } from 'components/property-editors/utils/ProviderNodeUtils';
import { SelectByIconEditorChip } from './SelectByIconEditorChip';
import { IconSelectionSet } from '@decisions/server/DecisionsFramework/Design/Properties';

// import './SelectByIconEditor.less'

export interface ISelectByIconEditorProps extends IEditorProps {}

const getClassName = () => classNames('dp-select-by-icon-editor');

/**
 * Editor which presents an icon to the user, not just a dropdown or a radio group.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function SelectByIconEditor({ child, path, id, onChange }: ISelectByIconEditorProps) {
  const nodeValue = getChildValueAsString(child);
  const [selectedValue, setSelectedValue] = useState(nodeValue);
  const values = getEditorConfigValue<IconSelectionSet[]>(child, 'values');
  const icons: Map<string, IconSelectionSet> = useMemo(() => {
    // convert to map once so we aren't iterating on every interaction
    const iconMap = new Map<string, IconSelectionSet>();
    values.forEach(val => iconMap.set(val.Value, val));
    return iconMap;
  }, [values]);

  const [hoveredValue, setHoveredValue] = useState('');

  useEffect(() => {
    if (selectedValue !== nodeValue) {
      setSelectedValue(nodeValue);
    }
    // update local state if value changes from the outside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeValue]);

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const newValue = e.currentTarget.id;
    setSelectedValue(newValue);
    onChange(newValue, path);
  };

  const onMouseIn = (e: React.MouseEvent<HTMLButtonElement>) => setHoveredValue(e.currentTarget.id);
  const onMouseOut = () => setHoveredValue('');

  return (
    <EditorLabel
      editor={child}
      path={path}
      htmlFor={id}
      className={getClassName()}
      labelTextClassName="dp-select-by-icon-editor__label"
      doNotWrap
    >
      {/* TODO should this be a custom input we wrap as an editor? */}
      <div className="dp-select-by-icon-editor__contents">
        {values.map(({ Value: value }) => (
          <SelectByIconEditorChip
            key={value}
            onClick={onClick}
            onMouseOver={onMouseIn}
            onMouseOut={onMouseOut}
            {...{ value, hoveredValue, icons, selectedValue }}
          />
        ))}
      </div>
    </EditorLabel>
  );
}
